import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatsPeriod } from '../api/carStatsApi';
import { useCarStats } from '../hooks/useCarStats';
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

export type StatsDisplayMode = 'compact' | 'detailed';

interface CarStatsCellProps {
  carId: string;
  mode?: StatsDisplayMode;
}

export const CarStatsCell: React.FC<CarStatsCellProps> = ({
  carId,
  mode = 'compact'
}) => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<StatsPeriod>('month');
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(new Date().getFullYear());
  const { data: stats, isLoading, deltaPercentage } = useCarStats(period, carId);

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.toLocaleString('en-US', { month: 'short' });
  const prevDate = new Date(now.getFullYear(), now.getMonth() - 1);
  const previousMonth = prevDate.toLocaleString('en-US', { month: 'short' });
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(currentYear, i).toLocaleString('en-US', { month: 'short' })
  );

  // Устанавливаем начальный период при первом рендере для детального режима
  React.useEffect(() => {
    if (mode === 'detailed') {
      setPeriod(`${currentMonth.toLowerCase()}-${currentYear}` as StatsPeriod);
    }
  }, [mode, currentMonth, currentYear]);

  const handleYearSelect = (year: number | 'all') => {
    setSelectedYear(year);
    if (year === 'all') {
      setPeriod('all');
    } else {
      if (year === currentYear) {
        setPeriod(`${currentMonth.toLowerCase()}-${year}` as StatsPeriod);
      } else {
        setPeriod(`dec-${year}` as StatsPeriod);
      }
    }
  };

  const handleEntireYearClick = () => {
    if (typeof selectedYear === 'number') {
      const newPeriod = selectedYear === currentYear ? 'current-year' : 'previous-year';
      setPeriod(newPeriod);
    }
  };

  const currentStats = stats;
  const hasData = !!currentStats;

  const busyDays = currentStats?.busy_days ?? 0;
  const freeDays = currentStats?.free_days ?? 0;
  const total = busyDays + freeDays;
  const busyPercentage = total > 0 ? Math.round((busyDays / total) * 1000) / 10 : 0;
  const freePercentage = total > 0 ? 100 - busyPercentage : 0;

  const isAllTimeSelected = selectedYear === 'all';
  const isEntireYearSelected = period === 'current-year' || period === 'previous-year';

  if (mode === 'compact') {
    return (
      <CompactView
        period={period}
        setPeriod={setPeriod}
        currentYear={currentYear}
        currentMonth={currentMonth}
        previousMonth={previousMonth}
        isLoading={isLoading}
        hasData={hasData}
        busyPercentage={busyPercentage}
        freePercentage={freePercentage}
        busyDays={busyDays}
        freeDays={freeDays}
        deltaPercentage={deltaPercentage}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Выбор года */}
      <div className="flex gap-2 text-xs">
        <button
          onClick={() => handleYearSelect(currentYear - 1)}
          className={`px-3 py-1.5 rounded ${
            selectedYear === currentYear - 1
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {currentYear - 1}
        </button>
        <button
          onClick={() => handleYearSelect(currentYear)}
          className={`px-3 py-1.5 rounded ${
            selectedYear === currentYear
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {currentYear}
        </button>
        <div className="w-[1px] bg-border-primary mx-1" />
        <button
          onClick={() => handleYearSelect('all')}
          className={`px-3 py-1.5 rounded ${
            isAllTimeSelected
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {t('cars.stats.all_time')}
        </button>
      </div>

      {/* Кнопка для всего года */}
      {!isAllTimeSelected && (
        <button
          onClick={handleEntireYearClick}
          className={`px-3 py-1.5 rounded text-sm ${
            isEntireYearSelected
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          }`}
        >
          {t('cars.stats.entire_year', { year: selectedYear })}
        </button>
      )}

      {/* Сетка месяцев */}
      {!isAllTimeSelected && (
        <div className="grid grid-cols-4 gap-2">
          {months.map(month => {
            // Создаем дату для проверки, не в будущем ли месяц
            const monthDate = new Date(selectedYear as number, months.indexOf(month));
            const isFutureMonth = monthDate > now;

            return (
              <button
                key={`${month}-${selectedYear}`}
                onClick={() => setPeriod(`${month.toLowerCase()}-${selectedYear}` as StatsPeriod)}
                disabled={isFutureMonth}
                className={`px-2 py-1 rounded text-sm ${
                  period === `${month.toLowerCase()}-${selectedYear}`
                    ? 'bg-brand-500 text-white'
                    : isFutureMonth
                    ? 'bg-bg-tertiary text-text-muted cursor-not-allowed'
                    : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
                }`}
              >
                {month}
              </button>
            );
          })}
        </div>
      )}

      <StatsDisplay
        hasData={hasData}
        isLoading={isLoading}
        busyPercentage={busyPercentage}
        freePercentage={freePercentage}
        busyDays={busyDays}
        freeDays={freeDays}
        deltaPercentage={deltaPercentage}
        mode="detailed"
      />
    </div>
  );
};

// Выносим отображение статистики в отдельный компонент
interface StatsDisplayProps {
  hasData: boolean;
  isLoading: boolean;
  busyPercentage: number;
  freePercentage: number;
  busyDays: number;
  freeDays: number;
  deltaPercentage: number | null;
  mode?: 'compact' | 'detailed';
}

const StatsDisplay: React.FC<StatsDisplayProps> = ({
  hasData,
  isLoading,
  busyPercentage,
  freePercentage,
  busyDays,
  freeDays,
  deltaPercentage,
  mode = 'compact'
}) => {
  const { t } = useTranslation();

  const options: ApexOptions = {
    colors: ["#FF3333"],
    chart: {
      fontFamily: "Outfit, sans-serif",
      type: "radialBar",
      height: 330,
      sparkline: {
        enabled: true,
      },
      animations: {
        enabled: true,
        speed: 300,
        animateGradually: {
          enabled: false
        },
        dynamicAnimation: {
          enabled: true,
          speed: 250
        }
      }
    },
    plotOptions: {
      radialBar: {
        startAngle: -85,
        endAngle: 85,
        hollow: {
          size: "80%",
        },
        track: {
          background: "#16A34A",
          strokeWidth: "100%",
          margin: 5,
          dropShadow: {
            enabled: false
          }
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            fontSize: "36px",
            fontWeight: "600",
            offsetY: -40,
            color: "#1D2939",
            formatter: function (val) {
              return val + "%";
            }
          },
        },
      },
    },
    fill: {
      type: "solid",
      colors: ["#FF3333"],
    },
    stroke: {
      lineCap: "round",
    },
    labels: ["Busy"],
    states: {
      hover: {
        filter: {
          type: 'none'
        }
      },
      active: {
        filter: {
          type: 'none'
        }
      }
    }
  };

  // Render delta percentage badge
  const renderDeltaBadge = () => {
    if (deltaPercentage === null || deltaPercentage === 0) return null;

    const isPositive = deltaPercentage > 0;
    const isNegative = deltaPercentage < 0;

    let bgColor = 'bg-gray-100 text-gray-600';
    let icon = null;

    if (isPositive) {
      // Positive delta means more busy days (green/positive)
      bgColor = 'bg-green-50 text-green-600';
      // Стрелка вверх для повышения занятости
      icon = (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-0.5">
          <path fillRule="evenodd" clipRule="evenodd" d="M7.60141 2.33683C7.73885 2.18084 7.9401 2.08243 8.16435 2.08243C8.16475 2.08243 8.16516 2.08243 8.16556 2.08243C8.35773 2.08219 8.54998 2.15535 8.69664 2.30191L12.6968 6.29924C12.9898 6.59203 12.9899 7.0669 12.6971 7.3599C12.4044 7.6529 11.9295 7.65306 11.6365 7.36027L8.91435 4.64004L8.91435 13.5C8.91435 13.9142 8.57856 14.25 8.16435 14.25C7.75013 14.25 7.41435 13.9142 7.41435 13.5L7.41435 4.64442L4.69679 7.36025C4.4038 7.65305 3.92893 7.6529 3.63613 7.35992C3.34333 7.06693 3.34348 6.59206 3.63646 6.29926L7.60141 2.33683Z" fill="currentColor" />
        </svg>
      );
    } else if (isNegative) {
      // Negative delta means fewer busy days (red/negative)
      bgColor = 'bg-red-50 text-red-600';
      // Стрелка вниз для понижения занятости
      icon = (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="ml-0.5">
          <path fillRule="evenodd" clipRule="evenodd" d="M7.26816 13.6632C7.4056 13.8192 7.60686 13.9176 7.8311 13.9176C7.83148 13.9176 7.83187 13.9176 7.83226 13.9176C8.02445 13.9178 8.21671 13.8447 8.36339 13.6981L12.3635 9.70076C12.6565 9.40797 12.6567 8.9331 12.3639 8.6401C12.0711 8.34711 11.5962 8.34694 11.3032 8.63973L8.5811 11.36L8.5811 2.5C8.5811 2.08579 8.24531 1.75 7.8311 1.75C7.41688 1.75 7.0811 2.08579 7.0811 2.5L7.0811 11.3556L4.36354 8.63975C4.07055 8.34695 3.59568 8.3471 3.30288 8.64009C3.01008 8.93307 3.01023 9.40794 3.30321 9.70075L7.26816 13.6632Z" fill="currentColor" />
        </svg>
      );
    }

    return (
      <span className={`absolute left-1/2 top-full -translate-x-1/2 -translate-y-[95%] rounded-full ${bgColor} px-3 py-1 text-xs font-medium flex items-center`}>
        {isPositive ? '+' : ''}{deltaPercentage}%
        {icon}
      </span>
    );
  };

  if (mode === 'compact') {
    return (
      <div className={`transition-all duration-200 ${isLoading ? 'opacity-50' : ''}`}>
        <div className="flex flex-col gap-1 p-2 -m-2">
          <div className="flex items-center gap-2">
            <div className={`h-2 flex rounded-full overflow-hidden bg-gray-100 w-full ${isLoading ? 'animate-pulse' : ''}`}>
              <div
                className="bg-green-500 transition-all duration-200"
                style={{ width: `${freePercentage}%` }}
              />
              <div
                className="bg-red-500 transition-all duration-200"
                style={{ width: `${busyPercentage}%` }}
              />
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap min-w-[4rem]">
              {hasData ? t('cars.stats.busy', { percentage: busyPercentage }) : t('cars.stats.no_data')}
            </span>
          </div>
          <div className="flex gap-2 text-xs">
            <span className="text-green-600">
              {t('cars.stats.free_days')}: {hasData ? `${freeDays}d` : '-'}
            </span>
            <span className="text-red-500">
              {t('cars.stats.busy_days')}: {hasData ? `${busyDays}d` : '-'}
            </span>
            {deltaPercentage !== null && deltaPercentage !== 0 && (
              <span className={`ml-auto ${deltaPercentage > 0 ? 'text-green-500' : 'text-red-500'}`}>
                {deltaPercentage > 0 ? '+' : ''}{deltaPercentage}%
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-bg-card shadow-default rounded-2xl">
      <div className="relative">
        <div className="max-h-[330px]">
          <Chart
            options={options}
            series={[busyPercentage]}
            type="radialBar"
            height={330}
          />
        </div>
        {renderDeltaBadge()}
      </div>

      <div className="flex items-center justify-center gap-5 px-6 py-3.5 sm:gap-8 sm:py-5">
        <div>
          <p className="mb-1 text-center text-text-tertiary text-theme-xs sm:text-sm">
            {t('cars.stats.busy_days')}
          </p>
          <p className="flex gap-2 items-center justify-center text-base font-semibold text-text-primary sm:text-lg">
            {busyDays}d
            <span className="w-2.5 h-2.5 rounded-full bg-red-600" />
          </p>
        </div>

        <div className="w-px bg-border-primary h-7"></div>

        <div>
          <p className="mb-1 text-center text-text-tertiary text-theme-xs sm:text-sm">
            {t('cars.stats.free_days')}
          </p>
          <p className="flex items-center justify-center gap-2 text-base font-semibold text-text-primary sm:text-lg">
            {freeDays}d
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </p>
        </div>
      </div>
    </div>
  );
};

interface CompactViewProps {
  period: StatsPeriod;
  setPeriod: (period: StatsPeriod) => void;
  currentYear: number;
  currentMonth: string;
  previousMonth: string;
  isLoading: boolean;
  hasData: boolean;
  busyPercentage: number;
  freePercentage: number;
  busyDays: number;
  freeDays: number;
  deltaPercentage: number | null;
}

const CompactView: React.FC<CompactViewProps> = ({
  period,
  setPeriod,
  currentYear,
  currentMonth,
  previousMonth,
  isLoading,
  hasData,
  busyPercentage,
  freePercentage,
  busyDays,
  freeDays,
  deltaPercentage
}) => {
  const { t } = useTranslation();
  const handlePeriodClick = (newPeriod: StatsPeriod) => {
    setPeriod(newPeriod);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 text-xs">
        <button
          onClick={() => handlePeriodClick('previous-month')}
          disabled={isLoading}
          className={`px-2 py-1 rounded ${
            period === 'previous-month'
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          } ${isLoading ? 'cursor-wait' : ''}`}
        >
          {previousMonth}
        </button>
        <button
          onClick={() => handlePeriodClick('month')}
          disabled={isLoading}
          className={`px-2 py-1 rounded ${
            period === 'month'
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          } ${isLoading ? 'cursor-wait' : ''}`}
        >
          {currentMonth}
        </button>
        <div className="w-[1px] bg-border-primary mx-1" />
        <button
          onClick={() => handlePeriodClick('current-year')}
          disabled={isLoading}
          className={`px-2 py-1 rounded ${
            period === 'current-year'
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          } ${isLoading ? 'cursor-wait' : ''}`}
        >
          {currentYear}
        </button>
        <button
          onClick={() => handlePeriodClick('previous-year')}
          disabled={isLoading}
          className={`px-2 py-1 rounded ${
            period === 'previous-year'
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          } ${isLoading ? 'cursor-wait' : ''}`}
        >
          {currentYear - 1}
        </button>
        <button
          onClick={() => handlePeriodClick('all')}
          disabled={isLoading}
          className={`px-2 py-1 rounded ${
            period === 'all'
              ? 'bg-brand-500 text-white'
              : 'bg-bg-tertiary text-text-secondary hover:bg-bg-hover'
          } ${isLoading ? 'cursor-wait' : ''}`}
        >
          {t('cars.stats.all_time')}
        </button>
      </div>
      <StatsDisplay
        hasData={hasData}
        isLoading={isLoading}
        busyPercentage={busyPercentage}
        freePercentage={freePercentage}
        busyDays={busyDays}
        freeDays={freeDays}
        deltaPercentage={deltaPercentage}
        mode="compact"
      />
    </div>
  );
};
